import { Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Menu, MenuButton, MenuItem, MenuItems, Transition } from "@headlessui/react";
import { StarIcon } from "@heroicons/react/24/outline";
import { useAppSelector } from "../../../app/hooks";
import { CourseState, clearCourse } from "../../../app/userSlice";
import { setCourse, transitionCourse } from "../../../app/userSlice";
import TextWithTip from "../../../components/TextWithTip";
import { useMakeCourseSelectionMutation } from "../../courses/coursesApiSlice";
import { SeeAllMenuItem } from "./Picker";

export default function CoursePicker() {
	const [callSelectCourse] = useMakeCourseSelectionMutation();
	const { course, courses } = useAppSelector((state) => state.user);
	const selectCourseURL = "/courses";
	const dispatch = useDispatch();
	const navigate = useNavigate();

	function handleCourseClick(course: CourseState) {
		dispatch(transitionCourse());
		callSelectCourse(course.id)
			.unwrap()
			.then((showAvatars) => {
				dispatch(setCourse({ ...course, showUserAvatars: showAvatars }));
				navigate(course.isActive ? "/home" : "/reports");
			})
			.catch((error) => {
				dispatch(clearCourse());
				navigate(selectCourseURL);
			});
	}

	if (!courses || !course) return null;

	const hasActiveCourse = courses.some((course) => course.isActive);

	return (
		<Menu as="div" className="relative">
			<div>
				<MenuButton
					id="courses-menu-button"
					className={`${
						course.isActive
							? "text-white font-bold underline underline-offset-4 decoration-1"
							: "text-indigo-100"
					} 
						font-medium cursor-pointer rounded-md bg-white/0 px-3 py-2 hover:bg-white/10 max-w-[240px]`}>
					<TextWithTip uniqueName={"courseName"} text={course.name ?? "Courses"} />
				</MenuButton>
			</div>
			<Transition
				as={Fragment}
				leave="transition ease-in duration-75"
				leaveFrom="transform opacity-100 scale-100"
				leaveTo="transform opacity-0 scale-95">
				<MenuItems className="border border-gray-200 bg-white absolute right-0 divide-y divide-gray-100 rounded-md min-w-[240px] z-50 overflow-hidden">
					<div>
						{courses
							.filter((item) => {
								return item.id !== course?.id && item.isActive === hasActiveCourse;
							})
							.sort((a, b) => {
								if (!!a.isFavorite !== !!b.isFavorite) {
									return !!a.isFavorite > !!b.isFavorite ? -1 : 1;
								}
								return a.name.localeCompare(b.name);
							})
							.map((item) => {
								return (
									<MenuItem key={item.id}>
										{({ active }) => (
											<span
												onClick={() => handleCourseClick(item)}
												className={`${
													active ? "bg-gray-100" : ""
												} flex items-center px-4 py-3 text-sm text-gray-800 hover:text-gray-700`}>
												<span className="w-4 text-center mr-2">
													<StarIcon
														className={`${
															item.isFavorite ? "fill-yellow-300" : ""
														} inline h-4 w-4 stroke-1`}
													/>
												</span>
												<span>{item.name}</span>
											</span>
										)}
									</MenuItem>
								);
							})}
					</div>
					<SeeAllMenuItem navigateURL={selectCourseURL} label={"courses"} />
				</MenuItems>
			</Transition>
		</Menu>
	);
}
